import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Pencil, Search, Trash2 } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../Input/Input.jsx";
import APITable from "../Tables/APITable.jsx";
import Pagination from "../Pagination/Pagination.jsx";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch.jsx";
import Button from "../Button/Button.jsx";
import Notification from "../Notification/Notification.jsx";
import ConfirmModal from "../ConfirmModal/ConfirmModal.jsx";
import { TableSkeleton } from "../Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut, apiDelete } from "../../apiService.js";
import { can } from "../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./ManageOrganisationSearch.module.scss";

const ManageOrganisationSearch = () => {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search & Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modals & Notifications
  const [contractModal, setContractModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  /* =========================
     HELPERS
  ========================= */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const buildOrganizationQuery = (pageNo, limit) => {
    const params = new URLSearchParams({
      page: String(pageNo),
      limit: String(limit),
    });

    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);

    return params.toString();
  };

  const formatOrganization = (organization) => ({
    id: organization.id,
    name: organization.name || "--",
    email: organization.email || "--",
    companyName: organization.companyName || "--",
    status: organization.status || "--",
    contractSigned: Boolean(organization.contractSigned),
  });

  /* =========================
     API CALLS
  ========================= */
  const fetchOrganizations = useCallback(
    async (pageNo = currentPage, limit = rowsPerPage) => {
      try {
        setLoading(true);

        const query = buildOrganizationQuery(pageNo, limit);
        const response = await apiGet("user", `/organizations?${query}`);

        if (response?.status === "success") {
          const responseData = response.data || {};
          const list = responseData.data || [];

          setOrganizations(list.map(formatOrganization));
          setPagination(
            responseData.pagination || {
              page: pageNo,
              limit,
              total: list.length,
              totalPages: 1,
            },
          );
        } else {
          showNotification("Failed to fetch organizations", "error");
        }
      } catch {
        showNotification("Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, rowsPerPage, search, statusFilter],
  );

  useEffect(() => {
    fetchOrganizations(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, statusFilter, fetchOrganizations]);

  /* =========================
     ACTIONS
  ========================= */
  const handleToggle = (row) => {
    setContractModal(row);
  };

  const confirmContractToggle = async () => {
    if (!contractModal) return;

    const row = contractModal;
    const nextContractSigned = !row.contractSigned;
    const previousRow = { ...row };

    // Optimistic UI Update
    setOrganizations((prev) =>
      prev.map((organization) =>
        organization.id === row.id
          ? { ...organization, contractSigned: nextContractSigned }
          : organization,
      ),
    );

    try {
      const response = await apiPut("user", "/organizations/contract", {
        id: row.id,
        contractSigned: nextContractSigned,
      });

      if (response?.status === "success") {
        showNotification("Contract status updated successfully");
      } else {
        throw new Error("Contract update failed");
      }
    } catch {
      // Revert on failure
      setOrganizations((prev) =>
        prev.map((organization) =>
          organization.id === row.id ? previousRow : organization,
        ),
      );
      showNotification("Contract status update failed", "error");
    } finally {
      setContractModal(null);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleView = (row) => navigate(`/admin/organization/view/${row.id}`);
  const handleEdit = (row) => navigate(`/admin/organization/edit/${row.id}`);
  const handleDelete = (row) => setDeleteModal(row);

  const confirmDelete = async () => {
    if (!deleteModal) return;

    setIsDeleting(true);
    try {
      const response = await apiDelete(
        "user",
        `/organizations/${deleteModal.id}`,
      );

      if (response?.status === "success") {
        showNotification(
          response.message || "Organization deleted successfully",
        );

        // If the last row on a page was removed, step back a page.
        const isLastRowOnPage =
          organizations.length === 1 && currentPage > 1;
        if (isLastRowOnPage) {
          setCurrentPage((prev) => prev - 1);
        } else {
          fetchOrganizations(currentPage, rowsPerPage);
        }
      } else {
        showNotification(
          response?.message || "Failed to delete organization",
          "error",
        );
      }
    } catch {
      showNotification("Something went wrong", "error");
    } finally {
      setIsDeleting(false);
      setDeleteModal(null);
    }
  };

  /* =========================
     TABLE CONFIG
  ========================= */
  const tableData = organizations.map((organization) => ({
    ...organization,
    onToggle: () => handleToggle(organization),
    onView: () => handleView(organization),
    onEdit: () => handleEdit(organization),
    onDelete: () => handleDelete(organization),
  }));

  const columns = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Company Name", key: "companyName" },
    { label: "Status", key: "status" },
    { label: "Contract Signed", key: "contractSigned" },
    { label: "Actions", key: "action" },
  ];

  /* =========================
     JSX
  ========================= */
  return (
    <>
      {notification && (
        <div className={styles["admin-manage-org-search__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["admin-manage-org-search__card"]}>
        {/* FILTER BAR */}
        <div className={styles["admin-manage-org-search__filter-bar"]}>
          <div className={styles["admin-manage-org-search__search-group"]}>
            <Input
              type="search"
              placeholder="Search Organization"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>

          <div className={styles["admin-manage-org-search__status-group"]}>
            <Input
              type="select"
              placeholder="All Statuses"
              value={statusFilter}
              onChange={(event) => {
                setCurrentPage(1);
                setStatusFilter(event.target.value);
              }}
              options={[
                { label: "All Statuses", value: "" },
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-manage-org-search__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                /* STATUS BADGE */
                if (key === "status") {
                  let badgeClass =
                    styles["admin-manage-org-search__badge--inactive"];
                  if (row.status?.toLowerCase() === "active") {
                    badgeClass =
                      styles["admin-manage-org-search__badge--active"];
                  }

                  return (
                    <span
                      className={`${styles["admin-manage-org-search__badge"]} ${badgeClass}`}
                    >
                      {row.status}
                    </span>
                  );
                }

                /* CONTRACT SIGNED BADGE */
                if (key === "contractSigned") {
                  const badgeClass = row.contractSigned
                    ? styles["admin-manage-org-search__badge--signed"]
                    : styles["admin-manage-org-search__badge--unsigned"];

                  return (
                    <span
                      className={`${styles["admin-manage-org-search__badge"]} ${badgeClass}`}
                    >
                      {row.contractSigned ? "Signed" : "Not Signed"}
                    </span>
                  );
                }

                /* ACTIONS */
                if (key === "action") {
                  return (
                    <div
                      className={styles["admin-manage-org-search__action-cell"]}
                    >
                      <Eye
                        size={20}
                        className={
                          styles["admin-manage-org-search__action-icon"]
                        }
                        onClick={row.onView}
                        aria-label="View Organization"
                        role="button"
                        tabIndex={0}
                      />
                      {can("organization", "update") && (
                        <Pencil
                          size={20}
                          className={
                            styles["admin-manage-org-search__action-icon"]
                          }
                          onClick={row.onEdit}
                          aria-label="Edit Organization"
                          role="button"
                          tabIndex={0}
                        />
                      )}
                      {can("organization", "delete") && (
                        <Trash2
                          size={20}
                          className={`${styles["admin-manage-org-search__action-icon"]} ${styles["admin-manage-org-search__action-icon--danger"]}`}
                          onClick={row.onDelete}
                          aria-label="Delete Organization"
                          role="button"
                          tabIndex={0}
                        />
                      )}
                      {can("organization", "contract") && (
                        <div title="Change contract signed status">
                          <ToggleSwitch
                            isOn={row.contractSigned}
                            onToggle={row.onToggle}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* CONFIRM MODAL */}
      <ConfirmModal
        open={!!contractModal}
        title="Are you sure you want to change the contract signed status?"
        onClose={() => setContractModal(null)}
        onConfirm={confirmContractToggle}
      />

      {/* DELETE CONFIRM MODAL */}
      <ConfirmModal
        open={!!deleteModal}
        title={
          deleteModal
            ? `Are you sure you want to delete "${deleteModal.companyName}"? This will also affect its users.`
            : "Are you sure you want to delete this organization?"
        }
        onClose={() => !isDeleting && setDeleteModal(null)}
        onConfirm={confirmDelete}
      />

    </>
  );
};

export default ManageOrganisationSearch;
